(() => {
  if (window.__nettCartConsentController) {
    window.__nettCartConsentController.bind();
    return;
  }

  const ATTRIBUTE_KEY = 'Rechtliche Hinweise bestätigt';
  const ATTRIBUTE_VALUE =
    'AGB akzeptiert; Widerrufsbelehrung und Datenschutz zur Kenntnis genommen';
  const ROOT_SELECTOR = '[data-nett-cart-consent]';
  const REQUEST_TIMEOUT_MS = 12000;
  let boundInput = null;
  let saveRequest = null;
  let generation = 0;
  let persisted = false;
  let consentError = '';
  let retrying = false;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getElements() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return null;
    return {
      root,
      input: root.querySelector('[data-nett-cart-consent-input]'),
      status: root.querySelector('[data-nett-cart-consent-status]'),
      error: root.querySelector('[data-nett-cart-consent-error]'),
      retry: root.querySelector('[data-nett-cart-retry]'),
      retryWrap: root.querySelector('[data-nett-cart-retry-wrap]'),
      checkout: document.querySelector('[data-nett-cart-checkout]'),
      dynamicCheckout: document.querySelector('[data-nett-dynamic-checkout]'),
    };
  }

  function propertiesState() {
    return window.__nhCartPropertiesState || { status: 'pending' };
  }

  function checkoutReady(elements) {
    return Boolean(
      elements?.input?.checked && persisted && !saveRequest && !retrying &&
      propertiesState().status === 'ready'
    );
  }

  function applyState() {
    const elements = getElements();
    if (!elements?.input) return;
    const properties = propertiesState();
    const saving = Boolean(saveRequest);
    const busy = saving || properties.status === 'pending' || retrying;
    const ready = checkoutReady(elements);
    const empty = elements.checkout?.dataset.nettCartEmpty === 'true' ||
      document.querySelector('cart-items')?.classList.contains('is-empty');
    const locked = !ready || Boolean(empty);
    const error = consentError || (properties.status === 'error'
      ? 'Deine Bestelldaten konnten nicht vorbereitet werden. Bitte versuche es erneut.' : '');

    elements.root.classList.toggle('is-confirmed', ready);
    elements.root.classList.toggle('is-saving', busy);
    elements.root.setAttribute('aria-busy', String(busy));
    elements.input.disabled = saving || retrying;
    if (elements.error) {
      elements.error.textContent = error;
      elements.error.hidden = !error;
    }
    if (elements.retry) {
      if (elements.retryWrap) elements.retryWrap.hidden = !error;
      else elements.retry.hidden = !error;
      elements.retry.disabled = busy;
    }
    if (elements.status) {
      const statusText = saving
        ? 'Bestätigung wird gespeichert...'
        : properties.status === 'pending'
          ? 'Deine Bestelldaten werden vorbereitet...'
          : error
            ? 'Der Checkout bleibt bis zur erfolgreichen Speicherung gesperrt.'
            : ready
              ? 'Bestätigt. Du kannst jetzt sicher zur Kasse gehen.'
              : 'Bitte einmal bestätigen, um den Checkout freizuschalten.';
      if (elements.status.textContent !== statusText) elements.status.textContent = statusText;
    }
    if (elements.checkout) {
      elements.checkout.disabled = locked;
      elements.checkout.setAttribute('aria-disabled', String(locked));
      if (busy) elements.checkout.setAttribute('aria-busy', 'true');
      else elements.checkout.removeAttribute('aria-busy');
    }
    if (elements.dynamicCheckout) {
      elements.dynamicCheckout.toggleAttribute('inert', locked);
      elements.dynamicCheckout.setAttribute('aria-disabled', String(locked));
    }
  }

  function invalidateRequest() {
    generation += 1;
    if (saveRequest) saveRequest.cancel();
    saveRequest = null;
    persisted = false;
  }

  function persistConsent(confirmed, operation) {
    const controller = new AbortController();
    let timer;
    let cancel;
    const deadline = new Promise((resolve, reject) => {
      cancel = () => {
        controller.abort();
        reject(new Error('Cart consent request cancelled.'));
      };
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Cart consent request timed out.'));
      }, REQUEST_TIMEOUT_MS);
    });
    operation.cancel = cancel;
    // The race also bounds a hanging response body or a fetch implementation that ignores abort.
    const request = Promise.resolve().then(() => fetch(`${cartRoot()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [ATTRIBUTE_KEY]: confirmed ? ATTRIBUTE_VALUE : '' } }),
    })).then((response) => {
      if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
      return response.json();
    }).then((cart) => {
      if (!cart || !cart.attributes || typeof cart.attributes !== 'object') {
        throw new Error('Cart update returned no attributes.');
      }
      const savedValue = cart.attributes[ATTRIBUTE_KEY];
      if (confirmed ? savedValue !== ATTRIBUTE_VALUE : savedValue != null && savedValue !== '') {
        throw new Error('Cart consent was not confirmed by the server.');
      }
      return cart;
    });
    return Promise.race([request, deadline]).finally(() => clearTimeout(timer));
  }

  function saveConsent(confirmed) {
    if (saveRequest) return saveRequest.promise;
    const operation = { id: ++generation, cancel: () => {}, promise: null };
    saveRequest = operation;
    persisted = false;
    consentError = '';
    applyState();
    operation.promise = persistConsent(confirmed, operation).then(() => {
      if (operation.id !== generation) return false;
      // Resetting an old attribute never counts as the customer's confirmation.
      persisted = confirmed && Boolean(getElements()?.input?.checked);
      return true;
    }).catch((error) => {
      if (operation.id !== generation) return false;
      console.error('Rechtliche Bestätigung konnte nicht gespeichert werden.', error);
      persisted = false;
      consentError = confirmed
        ? 'Die Bestätigung konnte nicht gespeichert werden. Bitte versuche es erneut.'
        : 'Die Bestätigung konnte nicht zurückgesetzt werden. Bitte versuche es erneut.';
      return false;
    }).finally(() => {
      if (operation.id !== generation) return;
      saveRequest = null;
      applyState();
    });
    return operation.promise;
  }

  function handleChange(event) {
    if (event.currentTarget !== boundInput || saveRequest || retrying) return;
    // In particular, a failed uncheck must never check the box again.
    persisted = false;
    saveConsent(event.currentTarget.checked);
  }

  function resetStoredConsent(elements) {
    invalidateRequest();
    consentError = '';
    elements.root.dataset.nettCartConsentReset = 'false';
    elements.input.checked = false;
    saveConsent(false);
  }

  async function handleRetry() {
    if (saveRequest || retrying || propertiesState().status === 'pending') return;
    retrying = true;
    applyState();
    try {
      if (consentError) await saveConsent(Boolean(getElements()?.input?.checked));
      const properties = propertiesState();
      if (properties.status === 'error' && typeof properties.retry === 'function') {
        await properties.retry();
      }
    } finally {
      retrying = false;
      applyState();
    }
  }

  function guardCheckout(event) {
    const elements = getElements();
    if (!elements?.input) return;
    if (event.type === 'submit') {
      const submitter = event.submitter;
      if (submitter?.getAttribute('name') === 'update') return;
      if (event.target !== elements.input.form && event.target?.id !== 'cart' &&
          submitter?.getAttribute('name') !== 'checkout') return;
    } else if (!event.target?.closest?.('[data-nett-cart-checkout], [data-nett-dynamic-checkout]')) {
      return;
    }
    if (checkoutReady(elements)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    applyState();
    if (!elements.input.checked && !consentError) {
      if (elements.error) {
        elements.error.textContent = 'Bitte bestätige zuerst die rechtlichen Hinweise.';
        elements.error.hidden = false;
      }
    }
    const focusTarget = !elements.input.disabled ? elements.input : elements.status;
    focusTarget?.focus({ preventScroll: true });
    elements.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function bind() {
    const elements = getElements();
    if (!elements?.input) {
      if (boundInput) invalidateRequest();
      boundInput = null;
      return;
    }
    if (elements.input !== boundInput) {
      invalidateRequest();
      consentError = '';
      boundInput = elements.input;
      boundInput.checked = false;
      boundInput.addEventListener('change', handleChange);
      elements.retry?.addEventListener('click', handleRetry);
    }
    if (elements.root.dataset.nettCartConsentReset === 'true') {
      resetStoredConsent(elements);
      return;
    }
    applyState();
  }

  window.__nettCartConsentController = { bind };
  document.addEventListener('submit', guardCheckout, true);
  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('nett:cart-properties-state', applyState);
  document.addEventListener('shopify:section:load', bind);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else bind();
  window.addEventListener('load', bind, { once: true });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const elements = getElements();
    if (elements?.input) resetStoredConsent(elements);
  });
  // Dawn can replace section contents without emitting shopify:section:load.
  new MutationObserver(() => {
    if (getElements()?.input !== boundInput) bind();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
